import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { bemClassName } from "../../utils/bemClassName";
import composePaginationLinkTo from "./composePaginationLinkTo";
import getPageOffset from "./getPageOffset";
import { joinClassNames } from "../../utils/joinClassNames";
import { useSearchQuestion, useSearchResults } from "../../context/DataContext";

import styles from "./PaginationItem.module.css";

export type ItemTypeValues = "button" | "link";

export type PaginationItemProps = {
    isCurrent?: boolean;
    itemType: ItemTypeValues;
    pageNumber: number;
    stepLabel?: string;
};

export const PaginationItem = ({
    isCurrent = false,
    itemType = "link",
    pageNumber,
    stepLabel = "page",
}: PaginationItemProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { pagination } = useSearchResults();
    const { setPage } = useSearchQuestion();
    const { limit } = pagination;

    const currentClass = isCurrent
        ? joinClassNames([
              bemClassName("ripple-news-listing", "pagination-step", "current"),
              styles.paginationItemsCurrent,
          ])
        : "";
    const disabledClass = isCurrent ? styles.disabled : "";
    const cssBlockName = "ripple-news-listing";
    const controlClasses = joinClassNames([
        bemClassName("ripple-news-listing", "pagination-step"),
        currentClass,
        disabledClass,
        styles.paginationItems,
    ]);

    const labelPrefix = isCurrent
        ? `Current ${stepLabel}: `
        : `Go to ${stepLabel} `;
    const label = (
        <React.Fragment>
            <span className="visuallyhidden">{labelPrefix.trim()}</span>{" "}
            {pageNumber}
        </React.Fragment>
    );

    const pageOffset = getPageOffset(pageNumber, limit);

    const isLink = itemType === "link";
    const linkTo =
        isLink && !isCurrent
            ? composePaginationLinkTo(
                  pageNumber,
                  pagination.limit,
                  searchParams
              )
            : "";

    const handleLinkClick = () => {
        setPage(pageNumber);
    };

    const handleButtonClick = () => {
        const newSearchParams = new URLSearchParams(searchParams);
        setPage(pageNumber);
        newSearchParams.set("start_rank", String(pageOffset));
        setSearchParams(newSearchParams);
    };

    return (
        <li className={bemClassName(cssBlockName, "pagination-item")}>
            {isLink ? (
                isCurrent ? (
                    <span className={controlClasses}>{label}</span>
                ) : (
                    <Link
                        to={linkTo}
                        className={controlClasses}
                        onClick={handleLinkClick}
                    >
                        {label}
                    </Link>
                )
            ) : (
                <button
                    type="button"
                    disabled={isCurrent}
                    className={controlClasses}
                    onClick={handleButtonClick}
                >
                    {label}
                </button>
            )}
        </li>
    );
};

export default PaginationItem;
